import {
  EditableText,
  EditableTypography,
  LabeledValue,
  RichTextValue,
} from "@msys/ui";
import { Warning as WarningIcon } from "@mui/icons-material";
import { Grid, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { NoPropagateBox } from "../../../commons/NoPropagateBox.js";
import { notSetSymbol } from "../../doc-items/helpers.js";
import {
  itemHasProperties,
  useQuoteItemProperties,
} from "../../item-properties/itemProperties.js";
import { TemplateQuotePreviewItemDescription_ItemFragment } from "./TemplateQuotePreviewItemDescription.generated.js";

export type TemplateQuotePreviewItemDescriptionEditing = {
  onChangeDescription: (value: string) => Promise<void> | void;
  onChangeProductTitle: (value: string) => Promise<void> | void;
  onChangeProductDescription: (value: string) => Promise<void> | void;
};

interface Props {
  item: TemplateQuotePreviewItemDescription_ItemFragment;
  columns?: number;
  // When set, the description / product title / product description become
  // inline-editable. Templates have no pending-change overlay — straightforward
  // swap to the matching editor when the field already has content.
  editing?: TemplateQuotePreviewItemDescriptionEditing;
}

export const TemplateQuotePreviewItemDescription = ({
  item,
  columns = 2,
  editing,
}: Props) => {
  const { t } = useTranslate(["Quote"]);
  const [properties, productProperties] = useQuoteItemProperties(item);

  const descriptionBody: React.ReactNode = (() => {
    if (editing && item.description) {
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableText
            value={item.description}
            placeholder={t("Description", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeDescription(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    if (item.description) {
      return (
        <RichTextValue key={item.description} htmlContent={item.description} />
      );
    }
    return null;
  })();

  const ItemDescription =
    descriptionBody || properties.length > 0 ? (
      <Stack direction="column" spacing={1}>
        {descriptionBody ? (
          <Typography
            variant="body2"
            component="div"
            sx={{ wordBreak: "break-word" }}
          >
            {descriptionBody}
          </Typography>
        ) : null}
        {properties.length > 0 ? (
          <Grid container columns={columns} spacing={1}>
            {properties.map(property => (
              <Grid key={property.key} size={1}>
                <LabeledValue
                  label={property.key}
                  labelIcon={
                    property.value === notSetSymbol ? WarningIcon : undefined
                  }
                  labelIconProps={
                    property.value === notSetSymbol
                      ? { color: "warning" }
                      : undefined
                  }
                >
                  {property.value}
                </LabeledValue>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Stack>
    ) : null;

  const productTitleCurrent =
    item.product &&
    item.product.title &&
    item.product.titleClientVisibility !== "hide"
      ? item.product.title
      : "";

  const ProductTitle: React.ReactNode = (() => {
    if (!productTitleCurrent || !item.product) return null;
    if (editing) {
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableTypography
            variant="h4"
            sx={{ fontWeight: "normal", wordBreak: "break-word" }}
            value={productTitleCurrent}
            allowEmpty={false}
            placeholder={t("Product title", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeProductTitle(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    return (
      <Typography
        variant="h4"
        sx={{ fontWeight: "normal", wordBreak: "break-word" }}
      >
        {productTitleCurrent}
      </Typography>
    );
  })();

  const productDescriptionCurrent =
    item.product &&
    item.product.description &&
    item.product.descriptionClientVisibility !== "hide"
      ? item.product.description
      : "";

  const productDescriptionBody: React.ReactNode = (() => {
    if (!productDescriptionCurrent || !item.product) return null;
    if (editing) {
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableText
            value={productDescriptionCurrent}
            placeholder={t("Product description", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeProductDescription(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    return (
      <RichTextValue
        key={productDescriptionCurrent}
        htmlContent={productDescriptionCurrent}
      />
    );
  })();

  const ProductDescription =
    productDescriptionBody || productProperties.length > 0 ? (
      <Stack direction="column" spacing={1}>
        {productDescriptionBody ? (
          <Typography
            variant="body2"
            component="div"
            sx={{ wordBreak: "break-word" }}
          >
            {productDescriptionBody}
          </Typography>
        ) : null}
        {productProperties.length > 0 ? (
          <Grid container columns={columns} spacing={1}>
            {productProperties.map(property => (
              <Grid key={property.key} size={1}>
                <LabeledValue label={property.key}>
                  {property.value}
                </LabeledValue>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Stack>
    ) : null;

  return ItemDescription || ProductTitle || ProductDescription ? (
    <Stack direction="column" spacing={1}>
      {ItemDescription}
      {ProductTitle}
      {ProductDescription}
    </Stack>
  ) : null;
};

export const templateQuoteItemHasDescription = (
  item: TemplateQuotePreviewItemDescription_ItemFragment
): boolean => {
  return Boolean(
    itemHasProperties(item) ||
    item.description ||
    (item.product &&
      item.product.title &&
      item.product.titleClientVisibility !== "hide") ||
    (item.product &&
      item.product.description &&
      item.product.descriptionClientVisibility !== "hide")
  );
};
